import { Component, OnInit } from '@angular/core';
import * as Enums from '@xbs/xbs-enums';
import { Observable } from 'rxjs';

import { ModalService, ClosableModal } from '../../../../shared/modules/modal';
import { CompSearchService } from '../../../../shared/services';
import {
  CompSearchContext,
  CompSearchRejectionReason,
  CopyrightLabels,
  EvaluateComparableTaxpayer,
  EvaluateComparableModalPayload,
  AppliedBulkRejection
} from '../../../../shared/models';
import { CopyrightConstants } from '../../../../shared/constants';
import { ListResponse } from 'src/app/shared/interfaces';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'evaluate-comparable-modal',
  templateUrl: './evaluate-comparable-modal.component.html',
  styleUrls: ['./evaluate-comparable-modal.component.scss']
})

export class EvaluateComparableModalComponent extends ClosableModal implements OnInit {

  data: EvaluateComparableModalPayload;
  evaluateTaxpayerData: EvaluateComparableTaxpayer = {} as EvaluateComparableTaxpayer;
  responseErrors: string[] = [];
  rejectionReasons: CompSearchRejectionReason[];
  areButtonsDisabled: boolean;
  primaryFunctions = Enums.PrimaryFunctions;
  allowedValueAdd: {};
  copyrightLabels: CopyrightLabels;
  fineTuningStatus: number;
  dataFetched: boolean = false;
  otherRejectionIds = [Enums.RejectionTypes.ByName.Other.Id, Enums.RejectionTypes.ByName.FineTuning.Id];

  constructor(
    private _compSearchService: CompSearchService,
    protected modalService: ModalService,
    private _translateService: TranslateService
  ) {
    super(modalService);

    this.allowedValueAdd = { 1: true, 0: true };
    this.areButtonsDisabled = true;
  }

  ngOnInit(): void {
    this.getEvaluateTaxPayer();
    const rejectionReasons = this.data.rejectionReasons || [];
    const primaryFunctionId = rejectionReasons[0].primaryFunctionId;

    this.rejectionReasons = rejectionReasons.filter((item) => {
      return Enums.PrimaryFunctions.ByType.Distributor.includes(primaryFunctionId) ?
        item.rejectionGroupTypeId === Enums.RejectionGroupTypes.ByName.FineTuning.Id :
        item.rejectionGroupTypeId === Enums.RejectionGroupTypes.ByName.FineTuningServiceProviders.Id;
    });
    
    this.rejectionReasons.forEach((rejection) => {
      rejection.rejectionName = this._translateService.instant(rejection.rejectionName);
    });
    this.rejectionReasons.sort((rejection1, rejection2) => {
      return rejection1.rejectionName > rejection2.rejectionName ? 1 : -1;
    });
  }

  shouldShowRejectionReasons(): boolean {
    return Number(this.fineTuningStatus) === Enums.FineTuningStatus.ByName.Rejected.Id;
  }

  shouldShowCurrentRejection(): boolean {
    return Number(this.fineTuningStatus) === Enums.FineTuningStatus.ByName.Rejected.Id &&
      !!this.evaluateTaxpayerData.rejectionName;
  }

  shouldShowValueAdd(): boolean {
    return this.allowedValueAdd[this.evaluateTaxpayerData.hasValueAdded];
  }

  shouldShowRetag(): boolean {
    return !this.allowedValueAdd[this.evaluateTaxpayerData.hasValueAdded] &&
      !this.evaluateTaxpayerData.discardReason;
  }

  shouldShowDiscarded(): boolean {
    return !this.allowedValueAdd[this.evaluateTaxpayerData.hasValueAdded] &&
      !!this.evaluateTaxpayerData.discardReason;
  }

  isTaxpayerFirstInList(): boolean {
    return this.data.currentCompInListIndex === 0;
  }

  isTaxpayerLastInList(): boolean {
    return this.data.currentCompInListIndex === (this.data.sourceIds.length - 1);
  }

  showTextBox(): boolean {
    const isRejected = Number(this.fineTuningStatus) === Enums.FineTuningStatus.ByName.Rejected.Id;
    const isFineTuning = Number(this.evaluateTaxpayerData.rejectionTypeIdNew) === Enums.RejectionTypes.ByName.FineTuning.Id;
    const isOther = Number(this.evaluateTaxpayerData.rejectionTypeIdNew) === Enums.RejectionTypes.ByName.Other.Id;

    return isRejected && (isFineTuning || isOther);
  }

  saveAndPrevious(): void {
    this.dataFetched = false;
    const isValidData = this.validateEvaluateComparableData();
    const canGoToPrevious = this.data.currentCompInListIndex > 0;

    if (isValidData && canGoToPrevious) {
      this.saveEvaluateComparableData().subscribe(
        () => {
          this.data.currentCompInListIndex -= 1;
          this.getEvaluateTaxPayer();
        },
        (error) => {
          this.fineTuningFailed(error);
        }
      );
    } else {
      this.dataFetched = true;
    }
  }

  saveAndNext(): void {
    this.dataFetched = false;
    const isValidData = this.validateEvaluateComparableData();
    const canGoToNext = this.data.currentCompInListIndex < (this.data.sourceIds.length - 1);

    if (isValidData && canGoToNext) {
      this.saveEvaluateComparableData().subscribe(
        () => {
          this.data.currentCompInListIndex += 1;
          this.getEvaluateTaxPayer();
        },
        (error) => {
          this.fineTuningFailed(error);
        }
      );
    } else {
      this.dataFetched = true;
    }
  }

  saveAndClose(): void {
    if (this.validateEvaluateComparableData()) {
      this.saveEvaluateComparableData().subscribe(
        () => {
          this.success();
          this.closeAllModals();
          this.dataFetched = true;
        },
        (error) => {
          this.fineTuningFailed(error);
          this.dataFetched = true;
        }
      );
    }
  }

  close(): void {
    this.success();
    this.closeAllModals();
    this.dataFetched = true;
  }

  private saveEvaluateComparableData(): Observable<ListResponse<AppliedBulkRejection>> {
    if (!this.areButtonsDisabled) {
      this.areButtonsDisabled = true;
      return this.applyFineTunning();
    }
  }

  private getEvaluateTaxPayer(): void {
    const context: CompSearchContext = CompSearchContext.new(this.data);
    context.setSourceIds([Number(this.data.sourceIds[this.data.currentCompInListIndex])]);

    this._compSearchService.getEvaluateTaxPayer(context).subscribe(({ data }) => {
      this.evaluateTaxpayerData = data[0];
      this.evaluateTaxpayerData.rejectionTypeIdNew = this.evaluateTaxpayerData.rejectionTypeId;
      if (this.evaluateTaxpayerData.isRejected === 0) {
        this.evaluateTaxpayerData.acceptedAndReviewed === 1 ? this.fineTuningStatus = 2 : this.fineTuningStatus = 0;
      } else {
        this.fineTuningStatus = 1;
      }
      this.areButtonsDisabled = false;
      this.setUpCopyrightInfo();
      this.dataFetched = true;
    }, (err) => {
      console.log(JSON.stringify(err, null, 2));
      this.areButtonsDisabled = false;
      this.dataFetched = true;
    });
  }

  private applyFineTunning(): Observable<ListResponse<AppliedBulkRejection>> {
    const context: CompSearchContext = CompSearchContext.new(this.data);
    context.setRejectionTypeId(Number(this.evaluateTaxpayerData.rejectionTypeIdNew));
    context.setSourceIds([Number(this.data.sourceIds[this.data.currentCompInListIndex])]);
    const otherRejectionReason = this.otherRejectionIds.includes(Number(this.evaluateTaxpayerData.rejectionTypeIdNew)) ? this.evaluateTaxpayerData.otherRejection : null;
    context.setOtherRejection(otherRejectionReason);
    context.isRejected = Number(this.fineTuningStatus) === Enums.FineTuningStatus.ByName.Rejected.Id ? 1 : 0;
    context.isAcceptedAndReviewed = Number(this.fineTuningStatus) === Enums.FineTuningStatus.ByName.AcceptedAndReviewed.Id ? 1 : 0;

    return this._compSearchService.updateCompFineTuningStatus(context);
  }

  private fineTuningFailed(err): void {
    console.log(JSON.stringify(err, null, 2));
    this.areButtonsDisabled = false;
    this.dataFetched = true;
  }

  private validateEvaluateComparableData(): boolean {
    const shouldReportMissingReason = Number(this.fineTuningStatus) === Enums.FineTuningStatus.ByName.Rejected.Id &&
      !this.evaluateTaxpayerData.rejectionTypeIdNew;
    this.responseErrors = [];

    if (shouldReportMissingReason) {
      this.responseErrors.push('Rejection Reason must be selected.');
      return false;
    }
    return true;
  }

  private setUpCopyrightInfo(): void {
    this.copyrightLabels = CopyrightConstants.Labels[this.evaluateTaxpayerData.dataSourceName];
  }
}
